import {Grid, Stack, Button, Menu, MenuItem, Typography, Box, useMediaQuery, useTheme} from "@mui/material";
import React, {useState} from "react";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowDown01Icon, Refresh01Icon} from "@hugeicons/core-free-icons";
import moment from "moment";
import {ConversationsTable} from "./components/ConversationsTable";
import {ConversationPreviewModal} from "./components/ConversationPreviewModal";
import {ConversationComment} from "./components/ConversationComment";
import {ConversationStatusGauge} from "./components/ConversationStatusGauge";
import {DashboardContextProvider, useDashboard} from "../../../context/DashboardContext";
import {SpeechToTextCard} from "./components/SpeechToTextCard";
import {TextToSpeechCard} from "./components/TextToSpeechCard";
import {ChatbotCard} from "./components/ChatbotCard";
import {ActiveCallsCard} from "./components/ActiveCallsCard";
import {TagsUsageChart} from "./components/tags-usage-chart";
import DateRangePickerWithMenu from "../../../components/date-range/DateRangePickerWithMenu";
import {Swiper, SwiperSlide} from "swiper/react";
import {Pagination} from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";


const REFRESH_INTERVAL_PRESETS = [
    {label: "Off", value: null},
    {label: "10s", value: 10000},
    {label: "30s", value: 30000},
    {label: "1 min", value: 60000},
    {label: "5 min", value: 300000},
];

const chipButtonSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.75,
    minWidth: 0,
    color: "text.primary",
    textTransform: "none",
    fontWeight: 400,
    border: "0 !important",
    boxShadow: "none",
    background: "#f4f4f4",
    borderRadius: "6px",
    padding: "4px 10px",
    fontSize: "11px",
    "&:hover": {
        bgcolor: "var(--surface-1, #FAFAF9)",
        boxShadow: "none",
        borderColor: "var(--border-strong, #C7C7C2)",
    },
};

const IntervalFilter = ({value, onChange}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const current = REFRESH_INTERVAL_PRESETS.find(p => p.value === value) || REFRESH_INTERVAL_PRESETS[0];

    return (
        <>
            <Button onClick={(e) => setAnchorEl(e.currentTarget)} sx={chipButtonSx}>
                <HugeiconsIcon icon={Refresh01Icon} size={16} color="#8A8A85" strokeWidth={1.75}/>
                {current.label}
                <HugeiconsIcon icon={ArrowDown01Icon} size={14} color="#A3A39E" strokeWidth={1.75}/>
            </Button>
            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
                {REFRESH_INTERVAL_PRESETS.map((preset) => (
                    <MenuItem
                        key={preset.label}
                        onClick={() => {
                            onChange(preset.value);
                            setAnchorEl(null);
                        }}
                    >
                        {preset.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export const ProjectDashboard = ({project}) => {
    return (
        <DashboardContextProvider project={project}>
            <ProjectDashboardContent project={project}/>
        </DashboardContextProvider>
    );
};

const ProjectDashboardContent = ({project}) => {
    const {
        statuses, tagUsage, dateRange, setDateRange,
        refreshInterval, setRefreshInterval, lastFetched
    } = useDashboard();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const type = project?.settings?.projectType || "VOICE_BOT"

    return (
        <Stack
          direction="column"
          sx={{
              height: "calc(100dvh - 49px)",
              maxHeight: "calc(100dvh - 49px)",
              flexShrink: 0,
              overflowY: {xs: "auto"},
              overflowX: {xs: "hidden", md: "visible"}
        }}
        >
            <ConversationPreviewModal project={project}/>
            <ConversationComment project={project}/>
            <Stack data-tour="dashboard-header" direction={"row"} justifyContent={"space-between"} alignItems={"center"}
                   sx={{px: {xs: 2, md: 3}, pt: {xs: 2, md: 2}, borderRadius: 2, flexShrink: 0, flexWrap: "wrap", rowGap: 1}}>
                {lastFetched ? (
                    <Stack direction={"row"} alignItems={"center"} gap={0.75}>
                        <Typography variant={"h5"} fontWeight={600}>Project Dashboard</Typography>
                        {/*<Box sx={{width: 6, height: 6, borderRadius: "50%", bgcolor: "#5BB98C", display: {xs: "none", md: "block"}}}/>*/}
                        {/*<Typography sx={{fontSize: 12.5, color: "text.secondary", display: {xs: "none", md: "block"}}}>*/}
                        {/*    Updated at {moment(lastFetched).format("HH:mm:ss")}*/}
                        {/*</Typography>*/}
                    </Stack>
                ) : <Box/>}
                <Stack data-tour="dashboard-filters" direction={"row"} alignItems={"center"} gap={1.5}>
                    <IntervalFilter value={refreshInterval} onChange={setRefreshInterval}/>
                    <DateRangePickerWithMenu
                        triggerSx={chipButtonSx}
                        defaultLabel={dateRange?.label}
                        onChange={setDateRange}
                    />
                </Stack>
            </Stack>
            {type === "VOICE_BOT" && (
                isMobile ? (
                    <Box sx={{
                        px: 2, pt: 1.5, flexShrink: 0, minWidth: 0, overflow: "hidden",
                        "& .swiper-pagination": {position: "static", mt: 1, lineHeight: 1},
                        "& .swiper-pagination-bullet": {width: 6, height: 6},
                        "& .swiper-pagination-bullet-active": {bgcolor: "primary.main"},
                    }}>
                        <Swiper modules={[Pagination]} pagination={{clickable: true}}
                                loop={true} slidesPerView={1} spaceBetween={12} style={{width: "100%", margin: 0}}>
                            <SwiperSlide>
                                <SpeechToTextCard/>
                            </SwiperSlide>
                            <SwiperSlide>
                                <TextToSpeechCard/>
                            </SwiperSlide>
                            <SwiperSlide>
                                <ChatbotCard/>
                            </SwiperSlide>
                            <SwiperSlide>
                                <ActiveCallsCard/>
                            </SwiperSlide>
                        </Swiper>
                    </Box>
                ) : (
                    <Grid container spacing={1.5}
                          sx={{px: {xs: 2, md: 3}, pt: 1.5, flexShrink: 0}}>
                        <Grid item xs={12} sm={6} lg={3}>
                            <SpeechToTextCard/>
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <TextToSpeechCard/>
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <ChatbotCard/>
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <ActiveCallsCard/>
                        </Grid>
                    </Grid>
                )
            )}

            <Grid container spacing={2}
                  sx={{px: {xs: 2, md: 3}, py: 2, flex: {xs: "0 0 auto", md: 1}, minHeight: 0}}>

                <Grid item xs={12} sm={6} md={12} lg={7} xl={8} sx={{height: {xs: 480, md: "100%"}, minHeight: "480px", minWidth: 0}}>
                    <ConversationsTable project={project}/>
                </Grid>

                <Grid data-tour="conversations-status" item xs={12} sm={6} md={6} lg={5} xl={4} sx={{height: {xs: 640, md: "100%"}, minWidth: 0}}>
                    <Stack direction={"column"} gap={2} sx={{height: "100%", minHeight: 480}}>
                        {/*<ConversationStatusPolar resolved={statuses?.RESOLVED || 0} unresolved={statuses?.UNRESOLVED || 0} missedCalls={statuses?.MISSED_CALL || 0} />*/}
                        <ConversationStatusGauge project={project} resolved={statuses?.RESOLVED || 0}
                                                 unresolved={statuses?.UNRESOLVED || 0}
                                                 missedCalls={statuses?.MISSED_CALL || 0} tagUsage={tagUsage}/>
                        <TagsUsageChart project={project}/>
                    </Stack>
                </Grid>

            </Grid>
        </Stack>
    )
}
